import { gql } from '@apollo/client';
import { Permission } from '@tp-vision/roles-permissions';
import { intersection } from 'lodash';
import { createContext, ReactNode, useMemo, useState } from 'react';
import { getElevatedOrganizationId } from '~utils/elevatedOrganization';
import {
  AuthorizationProvider_OrganizationFragment,
  GetMeQuery,
  useGetMeQuery,
} from './__generated__/AuthorizationProvider.graphql';
import { getServiceAdminPermissions } from './serviceAdminPermissions';
import { useAuthentication } from './useAuthentication';

interface AuthorizationContextProps {
  organization?: AuthorizationProvider_OrganizationFragment | null;
  setOrganization: (organization: AuthorizationProvider_OrganizationFragment) => void;
  verifyUserPermissions: (permissions: Permission[]) => boolean;
  me?: GetMeQuery['me'];
}

export const AuthorizationContext = createContext<AuthorizationContextProps | null>(null);

export function AuthorizationProvider({ children }: { children: ReactNode }) {
  const {
    user: { permissions: tokenPermissions = [], roles = [] },
  } = useAuthentication();

  // Inside another organization only the user's Service Admin roles count, as in the API.
  const userPermissions = useMemo(
    () =>
      getElevatedOrganizationId()
        ? getServiceAdminPermissions(roles, tokenPermissions)
        : tokenPermissions,
    [roles, tokenPermissions],
  );

  const [organization, setOrganization] =
    useState<AuthorizationProvider_OrganizationFragment | null>(null);

  const { data: meData } = useGetMeQuery();

  const verifyUserPermissions = (permissions: Permission[]) =>
    intersection(userPermissions, permissions).length > 0;

  const ctx: AuthorizationContextProps = {
    verifyUserPermissions,
    organization,
    setOrganization,
    me: meData?.me,
  };

  return <AuthorizationContext.Provider value={ctx}>{children}</AuthorizationContext.Provider>;
}

AuthorizationProvider.graphql = {
  fragments: gql`
    fragment AuthorizationProvider_organization on Organization {
      id
      name
      handle
      developerAccess
    }
  `,
  queries: gql`
    query GetMe {
      me {
        id
        email
        preferredConnection
      }
    }
  `,
};
